import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    makeStyles,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined'
import { useTranslation } from 'react-i18next'
import { setTenantUserPassword, TenantUserInfo } from '../../redux/actions/dashboard'

// rovnake pravidlo ako backend (PasswordService.TenantPasswordRegex) a Dashboard.tsx
export const validateTenantPass = (pass: string) => /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/.test(pass)

const LETTERS = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ'
const DIGITS = '23456789'
const SPECIALS = '!@#$%&*'

const randomIndex = (max: number) => {
    const buf = new Uint32Array(1)
    window.crypto.getRandomValues(buf)
    return buf[0] % max
}

// 12 znakov, vzdy aspon 1 cislica a 1 specialny znak
export const generatePassword = () => {
    const chars = [DIGITS[randomIndex(DIGITS.length)], SPECIALS[randomIndex(SPECIALS.length)]]
    const pool = LETTERS + DIGITS
    while (chars.length < 12) chars.push(pool[randomIndex(pool.length)])
    for (let i = chars.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1)
        ;[chars[i], chars[j]] = [chars[j], chars[i]]
    }
    return chars.join('')
}

const useStyles = makeStyles((theme) => ({
    field: {
        marginTop: theme.spacing(1),
        width: 360,
    },
    hint: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
}))

interface Props {
    user?: TenantUserInfo
    onClose: () => void
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

export default function SetPasswordDialog({ user, onClose, onSuccess, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setPassword('')
        setShowPassword(false)
    }, [user])

    const close = () => {
        if (!saving) onClose()
    }

    const handleSave = async () => {
        if (!user || !validateTenantPass(password)) return
        setSaving(true)
        try {
            await setTenantUserPassword(user.id, password)
            onSuccess(t('dashboard.passwordSetFor', { login: user.login }))
            onClose()
        } catch {
            onError(t('dashboard.errorSettingPass'))
        } finally {
            setSaving(false)
        }
    }

    const invalid = password.length > 0 && !validateTenantPass(password)

    return (
        <Dialog open={Boolean(user)} onClose={close}>
            <DialogTitle>{t('dashboard.setPasswordFor', { login: user?.login, tenant: user?.tenantName })}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    className={classes.field}
                    variant="filled"
                    label={t('common.newPassword')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    error={invalid}
                    helperText={invalid ? t('dashboard.errorPassValidation') : ' '}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave()
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {password ? (
                                    <Tooltip title={t('dashboard.copyPassword') as string}>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard?.writeText(password).catch(() => undefined)}
                                        >
                                            <FileCopyOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography variant="body2" className={classes.hint}>
                    {t('dashboard.setPasswordHint')}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setPassword(generatePassword())
                        setShowPassword(true)
                    }}
                    disabled={saving}
                >
                    {t('dashboard.generatePassword')}
                </Button>
                <div style={{ flex: 1 }} />
                <Button onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || !validateTenantPass(password)}
                >
                    {saving ? <CircularProgress size={20} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
